import * as React from "react";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp, toTimestamp } from "@racoon/lib";
import {
  createRouteTimelineSegments,
  findPositionAtTime,
  gpsPointsToTimelinePoints,
} from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { useGetTruckTravelPathQuery } from "~/shared/api/truck/truck.grpc";
import { MapViewSection } from "./map-view-section";
import { getDriverName, travelPathToGpsPoints } from "../lib/utils";
import type { TimelineSource, ViewMode } from "../model/types";
import { photoPointsForJobs } from "~/features/map-view/model/helpers/photo-points";
import { buildStepPins, numberRouteJobs } from "~/features/map-view/model/helpers/step-pin-items";

type Props = {
  shift: ConnectProto.DriverOnShift | undefined;
  truck: ConnectProto.Truck | undefined;
  /** The shift's trips — they build the timeline segments and the table. */
  routes: ConnectProto.Route[];
  /** Unload / depot locations to mark on the map. */
  depots?: ConnectProto.Location[];
  /** Track window; defaults to the shift's start → end (actual, else planned). */
  window?: { from: Date; to: Date } | null;
  /** Passed to `MapViewSection`: the host owns the map's top-right slot. */
  reserveTopRightSlot?: boolean;
  /**
   * The table lists only the trips' actual segments, with no source switch. The
   * ТТН page sets it once the TTN is approved — its times are then final.
   */
  factOnly?: boolean;
  /** Passed to `MapViewSection`: the time / speed / coordinates box while playing. */
  showPlaybackInfo?: boolean;
  /** The host's panel is collapsed: the bare map only, the table view switched off. */
  collapsed?: boolean;
  /** Passed to `MapViewSection`: off inside the photo report itself. */
  photosOpenReport?: boolean;
  /** Passed to `MapViewSection`: no frame of its own. */
  flush?: boolean;
};

/**
 * The "Контроль движения" map + timeline player + segments table, scoped to one
 * shift: the truck's track over the shift's window, played back against its trips.
 */
export const ShiftPlayback: React.FC<Props> = ({
  shift,
  truck,
  routes,
  depots = [],
  window: windowProp,
  reserveTopRightSlot,
  factOnly = false,
  showPlaybackInfo,
  collapsed = false,
  photosOpenReport,
  flush,
}) => {
  const t = useT();
  const [viewMode, setViewMode] = React.useState<ViewMode>("map");
  const [timelineSource, setTimelineSource] = React.useState<TimelineSource>("all");
  const [playbackTime, setPlaybackTime] = React.useState<number | null>(null);

  // Reset playback when another shift is opened.
  React.useEffect(() => setPlaybackTime(null), [shift?.id]);

  // From the shift start to its end (actual, else planned). Callers that know a
  // better window (the ТТН page) pass it in.
  const shiftWindow = React.useMemo(() => {
    if (windowProp !== undefined) return windowProp;
    if (!shift) return null;
    const from = fromTimestamp(shift.startedAt) ?? fromTimestamp(shift.createdAt);
    const to = fromTimestamp(shift.completedAt) ?? fromTimestamp(shift.twEndAt);
    return from && to ? { from, to } : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowProp, shift?.id, shift?.startedAt, shift?.completedAt]);

  // currentData: switching shifts must not replay the previous shift's path.
  const {
    currentData: travelPath,
    isFetching: isTrackFetching,
    isError: isTrackError,
  } = useGetTruckTravelPathQuery(
    {
      id: truck?.id ?? 0n,
      ownerOrganizationId: truck?.ownerOrganizationId ?? 0n,
      fromTo: shiftWindow
        ? { from: toTimestamp(shiftWindow.from), to: toTimestamp(shiftWindow.to) }
        : undefined,
    },
    { skip: !truck || !shiftWindow },
  );

  const selectedVehicle = React.useMemo(
    () => (truck && shift ? { ...truck, shifts: [shift] } : null),
    [truck, shift],
  );

  const gpsPoints = React.useMemo(
    () =>
      truck && shift
        ? travelPathToGpsPoints(travelPath?.geojsonFeature, truck, [getDriverName(shift)])
        : [],
    [travelPath, truck, shift],
  );

  const trackWindow = React.useMemo(
    () => ({
      from: gpsPoints[0]?.timestamp ?? null,
      to: gpsPoints[gpsPoints.length - 1]?.timestamp ?? null,
    }),
    [gpsPoints],
  );

  const pickupLocations = React.useMemo(() => {
    const byId = new Map<bigint, ConnectProto.PickupLocation>();
    for (const job of routes.flatMap((route) => route.jobs)) {
      if (job.pickupLocation) byId.set(job.pickupLocation.id, job.pickupLocation);
    }
    return [...byId.values()];
  }, [routes]);

  // Numbered like the trip tables beside the map: by step within each trip.
  const stepPins = React.useMemo(() => buildStepPins(numberRouteJobs(routes, "step")), [routes]);

  const routeDepots = React.useMemo(() => {
    const ids = new Set(routes.map((route) => route.destinationLocationId));
    return depots.filter((depot) => ids.has(depot.id));
  }, [depots, routes]);

  const photoPoints = React.useMemo(
    () => photoPointsForJobs(routes.flatMap((route) => route.jobs)),
    [routes],
  );

  const timelinePoints = React.useMemo(
    () => gpsPointsToTimelinePoints(gpsPoints, routes),
    [gpsPoints, routes],
  );

  const segments = React.useMemo(() => {
    const translate = (key: string, options?: Record<string, string | number>) =>
      t(Namespace.GpsTracking, key as never, options);
    const fact = createRouteTimelineSegments(routes, timelinePoints, translate, "fact");
    const gps = createRouteTimelineSegments(routes, timelinePoints, translate, "gps");
    return { fact, gps };
  }, [routes, timelinePoints, t]);

  const timelineSegments = segments.fact.length > 0 ? segments.fact : segments.gps;

  const tableSegments = React.useMemo(() => {
    if (factOnly || timelineSource === "fact") return segments.fact;
    if (timelineSource === "gps") return segments.gps;
    return [...segments.fact, ...segments.gps].sort((a, b) => a.startTime - b.startTime);
  }, [factOnly, timelineSource, segments]);

  const isPlaybackActive = playbackTime !== null;

  // While playing, the track is drawn up to the current moment only.
  const displayGpsPoints = React.useMemo(
    () =>
      isPlaybackActive
        ? gpsPoints.filter((point) => point.timestamp.getTime() <= playbackTime)
        : gpsPoints,
    [gpsPoints, isPlaybackActive, playbackTime],
  );

  const playbackPosition = React.useMemo(
    () =>
      isPlaybackActive && timelinePoints.length > 0
        ? findPositionAtTime(timelinePoints, playbackTime)
        : null,
    [isPlaybackActive, timelinePoints, playbackTime],
  );

  return (
    <MapViewSection
      viewMode={collapsed ? "map" : viewMode}
      onViewModeChange={setViewMode}
      gpsPoints={displayGpsPoints}
      trackWindow={trackWindow}
      selectedVehicle={selectedVehicle}
      pickupLocations={pickupLocations}
      stepPins={stepPins}
      depots={routeDepots}
      playbackPosition={playbackPosition}
      playbackTime={playbackTime}
      timelinePoints={timelinePoints}
      timelineSegments={timelineSegments}
      tableSegments={tableSegments}
      onTimeChange={setPlaybackTime}
      isPlaybackActive={isPlaybackActive}
      hasVehicles={Boolean(truck)}
      loading={isTrackFetching}
      trackError={isTrackError}
      timelineSource={factOnly ? "fact" : timelineSource}
      onTimelineSourceChange={factOnly ? undefined : setTimelineSource}
      reserveTopRightSlot={reserveTopRightSlot}
      showPlaybackInfo={showPlaybackInfo}
      hideChrome={collapsed}
      photoPoints={photoPoints}
      photosOpenReport={photosOpenReport}
      flush={flush}
    />
  );
};
